import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Shield, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { trpc } from '~/utils/trpc'
import { toast } from 'sonner'

/**
 * Where a tax ID's `verified: true` came from. `null`/`undefined` covers
 * companies verified before this column existed — treat those the same as
 * 'aeat_offline' (format-only), never as a registry confirmation, since we
 * have no record of what actually happened.
 *
 * 'vies_unnamed' (B-new-826 fix 1): the VAT number verified against VIES,
 * but VIES publishes no registered name at all for that member state (e.g.
 * Germany, unconditionally), so the name-match binding step could not run.
 * STRONGER than 'aeat_offline' (a real registry lookup did succeed) and
 * WEAKER than 'vies' (the name itself was never confirmed) — it gets its
 * own copy, not either extreme's.
 */
export type VerificationSource = 'vies' | 'aeat_live' | 'aeat_offline' | 'vies_unnamed' | null | undefined

interface NifVerificationCardProps {
  companyId: string
  currentTaxId: string | null
  isVerified: boolean
  verificationSource?: VerificationSource
  onVerified?: () => void
}

/**
 * 'vies' and 'aeat_live' mean a real registry / tax-authority lookup
 * confirmed the tax ID. Everything else — 'aeat_offline' (no AEAT
 * credential configured, checksum only) and unknown/null values — must NOT
 * render as a registry confirmation. This is an allowlist, not a denylist:
 * an unrecognised value falls through to the weaker, honest claim.
 */
export function isRegistryConfirmed(source: VerificationSource): boolean {
  return source === 'vies' || source === 'aeat_live'
}

/**
 * `vies_unnamed` is neither a registry confirmation (the name was never
 * checked) nor a plain format-only pass (a real VIES lookup DID succeed) —
 * it gets its own copy, distinct from both `isRegistryConfirmed` branches.
 */
export function isUnnamedRegistryMatch(source: VerificationSource): boolean {
  return source === 'vies_unnamed'
}

/**
 * `verifyTaxId` throws `TRPCError` with only FOUR distinct tRPC `code`s:
 * `SERVICE_UNAVAILABLE` (VIES down, retryable), `TOO_MANY_REQUESTS` (rate
 * limit), `FORBIDDEN` (caller isn't the company owner), and `BAD_REQUEST` /
 * `CONFLICT` for every other rejection reason (exemption-ID, bad format,
 * VIES not-found, registered-name mismatch, cannot-bind — see
 * apps/api/src/routers/company.ts). B-new-826 fix 3 adds a machine-readable
 * `reasonCode` on `err.data` (via `errorFormatter` in trpc.ts, additive —
 * never a rename of an existing field) that distinguishes those, so this
 * resolver branches on `reasonCode` FIRST and falls back to the tRPC `code`
 * only when no `reasonCode` is present (older server builds, or a genuinely
 * unclassified error). This is an ALLOWLIST, not message-string matching —
 * do NOT parse `err.message` (fragile, and an i18n'd server message would
 * break it outright); an unrecognised `reasonCode` falls through to the
 * generic `nif.notFound` copy, same as an unrecognised `code`.
 */
export function resolveVerifyErrorKey(
  code: string | undefined,
  reasonCode?: string,
): 'nif.serviceUnavailable' | 'nif.tooManyRequests' | 'nif.forbidden' | 'nif.exNumber' | 'nif.cannotBind' | 'nif.nameMismatch' | 'nif.notFound' {
  switch (reasonCode) {
    case 'ex_number':
      return 'nif.exNumber'
    case 'name_mismatch':
      return 'nif.nameMismatch'
    case 'cannot_bind':
      return 'nif.cannotBind'
    case 'invalid_format':
    case 'not_found_in_vies':
      return 'nif.notFound'
    default:
      break
  }
  switch (code) {
    case 'SERVICE_UNAVAILABLE':
      return 'nif.serviceUnavailable'
    case 'TOO_MANY_REQUESTS':
      return 'nif.tooManyRequests'
    case 'FORBIDDEN':
      return 'nif.forbidden'
    default:
      return 'nif.notFound'
  }
}

export function NifVerificationCard({ companyId, currentTaxId, isVerified, verificationSource, onVerified }: NifVerificationCardProps) {
  const { t } = useTranslation('employer')
  const [taxId, setTaxId] = useState(currentTaxId ?? '')

  const verifyMutation = trpc.company.verifyTaxId.useMutation({
    onSuccess: (result) => {
      toast.success(
        isRegistryConfirmed(result?.verificationSource)
          ? t('nif.verified')
          : isUnnamedRegistryMatch(result?.verificationSource)
            ? t('nif.verifiedTitleUnnamed')
            : t('nif.verifiedTitleFormat'),
      )
      onVerified?.()
    },
    onError: (err) => {
      toast.error(t(resolveVerifyErrorKey(err.data?.code, (err.data as { reasonCode?: string } | undefined)?.reasonCode)))
    },
  })

  if (isVerified) {
    const registryConfirmed = isRegistryConfirmed(verificationSource)
    const unnamedMatch = isUnnamedRegistryMatch(verificationSource)
    const titleKey = registryConfirmed
      ? 'nif.verifiedTitleRegistry'
      : unnamedMatch
        ? 'nif.verifiedTitleUnnamed'
        : 'nif.verifiedTitleFormat'
    const hintKey = registryConfirmed
      ? 'nif.verifiedRegistryHint'
      : unnamedMatch
        ? 'nif.verifiedUnnamedHint'
        : 'nif.verifiedFormatHint'
    const accentColor = registryConfirmed ? '#39ff14' : unnamedMatch ? '#ff00ff' : '#00f0ff'
    return (
      <div
        className="border-2 rounded-lg p-5 bg-[#0f0f15]/95"
        style={{ borderColor: `${accentColor}4d` /* ~30% alpha, matches the prior border color classes */ }}
      >
        <div className="flex items-center gap-3">
          {registryConfirmed ? (
            <CheckCircle className="w-5 h-5 text-[#39ff14]" />
          ) : (
            <Shield className={unnamedMatch ? 'w-5 h-5 text-[#ff00ff]' : 'w-5 h-5 text-[#00f0ff]'} />
          )}
          <div>
            <h3 className="text-white font-mono text-sm font-bold">
              {t(titleKey)}
            </h3>
            <p className="font-mono text-xs mt-1" style={{ color: accentColor }}>
              {currentTaxId}
            </p>
            <p className="text-[#a0a0b0] font-mono text-xs mt-1">
              {t(hintKey)}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="border-2 border-[#ff00ff]/30 bg-[#0f0f15]/95 rounded-lg p-5">
      <div className="flex items-center gap-3 mb-4">
        <Shield className="w-5 h-5 text-[#ff00ff]" />
        <h3 className="text-white font-mono text-sm font-bold">{t('nif.title')}</h3>
      </div>
      <p className="text-[#a0a0b0] font-mono text-xs mb-4">
        {t('nif.description')}
      </p>

      <div className="flex gap-3">
        <input
          type="text"
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          className="flex-1 bg-[#0a0a0f] border-2 border-[#ff00ff]/30 rounded-lg px-4 py-2 text-white font-mono text-sm focus:border-[#ff00ff] focus:outline-none transition-colors placeholder:text-[#3f3f45]"
          placeholder="B12345678"
        />
        <button
          type="button"
          onClick={() => verifyMutation.mutate({ companyId, taxId: taxId.trim() })}
          disabled={verifyMutation.isPending || !taxId.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-[#ff00ff] text-[#0a0a0f] hover:bg-[#00f0ff] disabled:opacity-50 disabled:cursor-not-allowed transition-all font-mono text-xs font-bold rounded-lg"
        >
          {verifyMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Shield className="w-4 h-4" />
          )}
          {t('nif.verify')}
        </button>
      </div>

      {verifyMutation.error && (
        <div className="mt-3 flex items-center gap-2 text-[#ff00ff] font-mono text-xs">
          <XCircle className="w-4 h-4" />
          {t(resolveVerifyErrorKey(verifyMutation.error.data?.code, (verifyMutation.error.data as { reasonCode?: string } | undefined)?.reasonCode))}
        </div>
      )}

      {verifyMutation.data?.companyName && (
        <div className="mt-3 flex items-center gap-2 text-[#39ff14] font-mono text-xs">
          <CheckCircle className="w-4 h-4" />
          {verifyMutation.data.companyName}
        </div>
      )}
    </div>
  )
}
